import type {
    CommerceAddress,
    CommerceCart,
    CommerceCheckout,
    CommerceCheckoutContact,
    CommerceCheckoutItem,
    CommerceCheckoutValidationMessage,
    CommerceMoney,
    CommerceCheckoutPaymentConfiguration,
    CommerceShippingMethod,
    CommerceOrder,
    SelectCommerceCheckoutShippingMethodRequest,
    UpdateCommerceCheckoutAddressRequest,
    UpdateCommerceCheckoutContactRequest,
} from "@teqtank/commerce-contracts";
import { MockCommerceTransport } from "@teqtank/commerce-transport";
import { beginCommerceCheckoutOperation } from "../operations/begin-commerce-checkout-operation.js";
import { applyCommerceCheckoutCustomerOperation } from "../operations/apply-commerce-checkout-customer-operation.js";
import { selectCommerceCheckoutSavedPaymentMethodOperation } from "../operations/select-commerce-checkout-saved-payment-method-operation.js";
import { calculateCommerceCheckoutOperation } from "../operations/calculate-commerce-checkout-operation.js";
import { getCommerceCheckoutOperation } from "../operations/get-commerce-checkout-operation.js";
import { selectCommerceCheckoutShippingMethodOperation } from "../operations/select-commerce-checkout-shipping-method-operation.js";
import { submitCommerceCheckoutOperation } from "../operations/submit-commerce-checkout-operation.js";
import { updateCommerceCheckoutAddressOperation } from "../operations/update-commerce-checkout-address-operation.js";
import { updateCommerceCheckoutContactOperation } from "../operations/update-commerce-checkout-contact-operation.js";
import { clearCommerceCheckoutCustomerDetailsOperation } from "../operations/clear-commerce-checkout-customer-details-operation.js";
import { getCommerceAddressRegionsOperation } from "../operations/get-commerce-address-regions-operation.js";
import { getCommerceCheckoutPaymentConfigurationOperation } from "../operations/get-commerce-checkout-payment-configuration-operation.js";
import { selectCommerceCheckoutPaymentOptionOperation } from "../operations/select-commerce-checkout-payment-option-operation.js";
import { applyCommerceCheckoutPointsOperation } from "../operations/apply-commerce-checkout-points-operation.js";
import { completeCommerceCheckoutOperation } from "../operations/complete-commerce-checkout-operation.js";

export function registerDefaultCheckoutOperations(
    transport: MockCommerceTransport,
    getCart: () => CommerceCart,
    getExperience: () => "shop" | "enrollment" = () => "shop",
): () => CommerceCheckout | undefined {
    let checkout: MutableCheckout | undefined;

    transport.register(getCommerceAddressRegionsOperation, ({ countryCode }) => ({
        regions: playgroundRegions(countryCode),
    }));

    transport.register(beginCommerceCheckoutOperation, () => {
        const cart = getCart();
        if (cart.lines.length === 0) throw new Error("Checkout requires at least one Cart line.");
        checkout = createCheckout(cart, getExperience());
        return { checkout: materialize(checkout, getCart()) };
    });

    transport.register(getCommerceCheckoutOperation, () => ({
        checkout: checkout === undefined ? null : materialize(checkout, getCart()),
    }));
    transport.register(updateCommerceCheckoutContactOperation, (request) => ({
        checkout: mutate(
            requireCheckout(checkout),
            request.expectedVersion,
            (current) => {
                current.contact = normalizeContact(request);
            },
            getCart,
        ),
    }));
    transport.register(clearCommerceCheckoutCustomerDetailsOperation, (request) => ({
        checkout: mutate(
            requireCheckout(checkout),
            request.expectedVersion,
            (current) => {
                current.contact = null;
                current.shippingAddress = null;
                current.billingAddress = null;
                current.billingAddressSameAsShipping = true;
                current.selectedSavedPaymentMethodId = null;
            },
            getCart,
        ),
    }));
    transport.register(updateCommerceCheckoutAddressOperation, (request) => ({
        checkout: mutate(
            requireCheckout(checkout),
            request.expectedVersion,
            (current) => updateAddress(current, request),
            getCart,
        ),
    }));
    transport.register(selectCommerceCheckoutShippingMethodOperation, (request) => ({
        checkout: mutate(
            requireCheckout(checkout),
            request.expectedVersion,
            (current) => selectShipping(current, request),
            getCart,
        ),
    }));
    transport.register(applyCommerceCheckoutCustomerOperation, () => ({
        checkout: materialize(requireCheckout(checkout), getCart()),
    }));
    transport.register(selectCommerceCheckoutSavedPaymentMethodOperation, (request) => {
        const current = requireCheckout(checkout);
        current.selectedSavedPaymentMethodId = request.paymentMethodId;
        current.version++;
        return { checkout: materialize(current, getCart()) };
    });
    transport.register(getCommerceCheckoutPaymentConfigurationOperation, () => ({
        payment: paymentConfiguration(requireCheckout(checkout), getCart()),
    }));
    transport.register(selectCommerceCheckoutPaymentOptionOperation, (request) => {
        const current = requireCheckout(checkout);
        current.selectedPaymentOptionId = request.paymentOptionId;
        current.selectedSavedPaymentMethodId = request.savedPaymentMethodId;
        current.version++;
        return {
            checkout: materialize(current, getCart()),
            payment: paymentConfiguration(current, getCart()),
        };
    });
    transport.register(applyCommerceCheckoutPointsOperation, () => ({
        checkout: materialize(requireCheckout(checkout), getCart()),
        payment: paymentConfiguration(requireCheckout(checkout), getCart()),
    }));
    transport.register(calculateCommerceCheckoutOperation, (request) => ({
        checkout: mutate(
            requireCheckout(checkout),
            request.expectedVersion,
            () => undefined,
            getCart,
        ),
    }));
    transport.register(submitCommerceCheckoutOperation, () => {
        if (checkout === undefined) throw new Error("Checkout has not been started.");
        const materialized = materialize(checkout, getCart());
        return {
            checkout: materialized,
            attemptId: `attempt:${materialized.id}:${materialized.version}`,
            paymentOrder: Object.freeze({
                checkoutId: materialized.id,
                amount: materialized.totals.total.amount,
            }),
        };
    });
    transport.register(completeCommerceCheckoutOperation, ({ paymentResult }) => {
        if (checkout === undefined) throw new Error("Checkout has not been started.");
        const current = materialize(checkout, getCart());
        const provider =
            typeof paymentResult === "object" && paymentResult !== null
                ? (paymentResult as Readonly<Record<string, unknown>>)
                : (Object.freeze({}) as Readonly<Record<string, unknown>>);
        const orderId =
            typeof provider.orderId === "string" ? provider.orderId : `demo:${current.id}`;
        const now = new Date();
        const order: CommerceOrder = Object.freeze({
            id: orderId,
            orderNumber: orderId,
            status: "completed",
            currencyCode: current.totals.total.currencyCode,
            email: current.contact?.email ?? "",
            items: Object.freeze(
                current.items.map((item) =>
                    Object.freeze({
                        id: item.id,
                        productId: item.productId,
                        variantId: item.variantId,
                        ...(item.displaySku === undefined ? {} : { displaySku: item.displaySku }),
                        name: item.name,
                        image: item.image,
                        quantity: item.quantity,
                        unitPrice: item.unitPrice,
                        lineTotal: item.lineTotal,
                    }),
                ),
            ),
            totals: current.totals,
            shippingAddress: current.shippingAddress,
            billingAddress: current.billingAddress,
            shipments: Object.freeze([]),
            receipt: null,
            paymentSummary: "Demo payment",
            failureMessage: null,
            createdAt: now,
            updatedAt: now,
            completedAt: now,
        });
        return { order, redirectUrl: null, warnings: Object.freeze([]) };
    });
    return () => (checkout === undefined ? undefined : materialize(checkout, getCart()));
}

function playgroundRegions(countryCode: string) {
    const regions: Record<string, readonly { code: string; name: string }[]> = {
        NZ: [
            { code: "AUK", name: "Auckland" },
            { code: "WGN", name: "Wellington" },
            { code: "CAN", name: "Canterbury" },
        ],
        US: [
            { code: "AZ", name: "Arizona" },
            { code: "CA", name: "California" },
            { code: "NY", name: "New York" },
        ],
    };
    return regions[countryCode.trim().toUpperCase()] ?? [];
}

interface MutableCheckout {
    id: string;
    experience: "shop" | "enrollment";
    version: number;
    contact: CommerceCheckoutContact | null;
    shippingAddress: CommerceAddress | null;
    billingAddress: CommerceAddress | null;
    billingAddressSameAsShipping: boolean;
    selectedShippingMethodId: string | null;
    selectedSavedPaymentMethodId: string | null;
    selectedPaymentOptionId: string | null;
}

function createCheckout(cart: CommerceCart, experience: "shop" | "enrollment"): MutableCheckout {
    return {
        id: `checkout:${cart.id}`,
        experience,
        version: 1,
        contact: null,
        shippingAddress: null,
        billingAddress: null,
        billingAddressSameAsShipping: true,
        selectedShippingMethodId: "standard",
        selectedSavedPaymentMethodId: null,
        selectedPaymentOptionId: "card",
    };
}

function paymentConfiguration(
    checkout: MutableCheckout,
    cart: CommerceCart,
): CommerceCheckoutPaymentConfiguration {
    const current = materialize(checkout, cart);
    return Object.freeze({
        options: Object.freeze([
            {
                id: "card",
                name: "Credit or debit card",
                description: "Secure card payment",
                recurringSupported: true,
            },
        ]),
        selectedPaymentOptionId: checkout.selectedPaymentOptionId,
        selectedSavedPaymentMethodId: checkout.selectedSavedPaymentMethodId,
        frameSource: null,
        points: Object.freeze({ available: money(0), applied: money(0), enabled: false }),
        amountDue: current.totals.total,
    });
}

function requireCheckout(value: MutableCheckout | undefined): MutableCheckout {
    if (value === undefined) throw new Error("Checkout has not been started.");
    return value;
}

function mutate(
    checkout: MutableCheckout,
    expectedVersion: number | undefined,
    action: (checkout: MutableCheckout) => void,
    getCart: () => CommerceCart,
): CommerceCheckout {
    if (expectedVersion !== undefined && expectedVersion !== checkout.version)
        throw new Error(
            `Checkout version conflict. Expected ${expectedVersion}, current ${checkout.version}.`,
        );
    action(checkout);
    checkout.version += 1;
    return materialize(checkout, getCart());
}

function normalizeContact(request: UpdateCommerceCheckoutContactRequest): CommerceCheckoutContact {
    return Object.freeze({
        email: request.contact.email.trim().toLowerCase(),
        phone: request.contact.phone.trim(),
        acceptsMarketing: request.contact.acceptsMarketing,
    });
}

function updateAddress(
    checkout: MutableCheckout,
    request: UpdateCommerceCheckoutAddressRequest,
): void {
    const address = Object.freeze({ ...request.address });
    if (request.addressType === "shipping") {
        checkout.shippingAddress = address;
        checkout.billingAddressSameAsShipping =
            request.billingAddressSameAsShipping ?? checkout.billingAddressSameAsShipping;
        if (checkout.billingAddressSameAsShipping) checkout.billingAddress = address;
        return;
    }
    checkout.billingAddress = address;
    checkout.billingAddressSameAsShipping = false;
}

function selectShipping(
    checkout: MutableCheckout,
    request: SelectCommerceCheckoutShippingMethodRequest,
): void {
    if (
        !shippingMethods(request.shippingMethodId).some(
            (item) => item.id === request.shippingMethodId,
        )
    )
        throw new Error(`Shipping method '${request.shippingMethodId}' is invalid.`);
    checkout.selectedShippingMethodId = request.shippingMethodId;
}

function materialize(checkout: MutableCheckout, cart: CommerceCart): CommerceCheckout {
    const methods = shippingMethods(checkout.selectedShippingMethodId);
    const selected = methods.find((item) => item.selected) ?? methods[0];
    const shippingAmount = selected?.price.amount ?? 0;
    const items = calculateItems(cart);
    const subtotal = round(items.reduce((sum, item) => sum + item.lineSubtotal.amount, 0));
    const discount = round(items.reduce((sum, item) => sum + item.lineDiscount.amount, 0));
    const itemTotal = round(items.reduce((sum, item) => sum + item.lineTotal.amount, 0));
    const validation = validate(checkout, cart);
    const status = validation.isValid ? "payment-required" : "draft";

    return Object.freeze({
        id: checkout.id,
        experience: checkout.experience,
        version: checkout.version,
        status,
        cart,
        contact: checkout.contact,
        shippingAddress: checkout.shippingAddress,
        billingAddress: checkout.billingAddress,
        billingAddressSameAsShipping: checkout.billingAddressSameAsShipping,
        shippingMethods: Object.freeze(methods),
        selectedShippingMethodId: checkout.selectedShippingMethodId,
        selectedSavedPaymentMethodId: checkout.selectedSavedPaymentMethodId,
        items: Object.freeze(items),
        upsells: Object.freeze([]),
        totals: Object.freeze({
            subtotal: money(subtotal),
            discount: money(discount),
            shipping: money(shippingAmount),
            itemTax: money(0),
            shippingTax: money(0),
            tax: money(0),
            total: money(round(itemTotal + shippingAmount)),
        }),
        validation,
        updatedAt: new Date(),
    });
}

function calculateItems(cart: CommerceCart): CommerceCheckoutItem[] {
    return cart.lines.map((line) =>
        Object.freeze({
            id: line.id,
            productId: line.productId,
            variantId: line.variantId,
            ...(line.displaySku === undefined ? {} : { displaySku: line.displaySku }),
            name: line.name,
            image: line.image,
            purchaseType: line.purchaseType,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            lineSubtotal: line.lineSubtotal,
            lineDiscount: line.lineDiscount,
            lineTotal: line.lineTotal,
            source: "cart" as const,
            ...(line.components === undefined ? {} : { components: line.components }),
        }),
    );
}

function validate(checkout: MutableCheckout, cart: CommerceCart) {
    const messages: CommerceCheckoutValidationMessage[] = [];
    if (cart.lines.length === 0) messages.push(message("empty-cart", "Your Cart is empty."));
    if (checkout.contact === null || !/^\S+@\S+\.\S+$/.test(checkout.contact.email))
        messages.push(message("email-required", "Enter a valid email address.", "contact.email"));
    validateAddress(checkout.shippingAddress, "shippingAddress", messages);
    if (!checkout.billingAddressSameAsShipping)
        validateAddress(checkout.billingAddress, "billingAddress", messages);
    if (checkout.selectedShippingMethodId === null)
        messages.push(
            message("shipping-required", "Select a delivery method.", "selectedShippingMethodId"),
        );
    const errors = Object.freeze(messages.filter((item) => item.severity === "error"));
    const warnings = Object.freeze(messages.filter((item) => item.severity === "warning"));
    return Object.freeze({
        isValid: errors.length === 0,
        hasErrors: errors.length > 0,
        errors,
        warnings,
    });
}

function validateAddress(
    address: CommerceAddress | null,
    prefix: string,
    messages: CommerceCheckoutValidationMessage[],
): void {
    if (address === null) {
        messages.push(message("address-required", "Enter a delivery address.", prefix));
        return;
    }
    for (const [field, value] of [
        ["firstName", address.firstName],
        ["lastName", address.lastName],
        ["addressLine1", address.addressLine1],
        ["city", address.city],
        ["postalCode", address.postalCode],
        ["countryCode", address.countryCode],
    ] as const) {
        if (value.trim().length === 0)
            messages.push(message("field-required", `${field} is required.`, `${prefix}.${field}`));
    }
}

function message(code: string, text: string, field?: string): CommerceCheckoutValidationMessage {
    return Object.freeze({
        code,
        message: text,
        ...(field !== undefined ? { field } : {}),
        severity: "error",
    });
}

function shippingMethods(selectedId: string | null): CommerceShippingMethod[] {
    return [
        Object.freeze({
            id: "standard",
            name: "Standard delivery",
            description: "Tracked delivery",
            estimatedDelivery: "3–5 business days",
            price: money(0),
            selected: selectedId === "standard",
        }),
        Object.freeze({
            id: "express",
            name: "Express delivery",
            description: "Priority tracked delivery",
            estimatedDelivery: "1–2 business days",
            price: money(0),
            selected: selectedId === "express",
        }),
    ];
}
function money(amount: number): CommerceMoney {
    return Object.freeze({
        amount,
        currencyCode: "NZD",
        formatted: `$${amount.toFixed(2)}`,
    });
}
function round(value: number): number {
    return Math.round(value * 100) / 100;
}
